import { CampoDeSelecao } from '../../components/campos'
import { Explicacao } from '../../components/Explicacao'
import { TERMOS, TEXTOS } from '../../lib/glossario'
import type { CompetenciaComDado } from '../../types/chamado'
import { competenciaPorExtenso } from './competencia'

interface Props {
  competencias: CompetenciaComDado[]
  valor: string
  aoEscolher: (competencia: string) => void
  /** Texto da opção que não escolhe mês nenhum. Ausente = a escolha é obrigatória. */
  rotuloDeTodas?: string
  carregando?: boolean
}

/**
 * O seletor de mês de referência, usado pela listagem e pelo fechamento.
 *
 * Só oferece meses que têm dado carregado, e é o servidor que diz quais são
 * (`GET /chamados/competencias`). A alternativa — gerar os últimos doze meses — deixaria o fiscal
 * escolher agosto e receber um fechamento de nada, que é indistinguível de um fechamento sem
 * ocorrências. Aqui, mês que não aparece é mês que ninguém carregou.
 *
 * Cada opção diz quantos chamados fecharam no mês e, quando a coleta não alcança o fim dele, marca
 * isso no próprio texto da opção. É a informação que decide se dá para assinar o mês: um fechamento
 * apurado sobre coleta parcial sai com aparência de certo, e a hora de saber disso é antes de
 * escolher, não depois de ler o número.
 *
 * @param props as competências disponíveis, a escolhida e o que fazer ao trocar
 * @returns o campo de escolha do mês
 */
export function SeletorDeCompetencia({ competencias, valor, aoEscolher, rotuloDeTodas, carregando }: Props) {
  return (
    <CampoDeSelecao
      rotulo={TERMOS.competencia.rotulo}
      // O critério do mês (fechado, e não aberto) é longo e lido uma vez; a linha que fica à vista
      // é a que se relê a cada visita. Escrito por extenso aqui, o parágrafo empurrava o resto da
      // tela para baixo em três telas — fechamento, pontos administrativos e relatórios.
      ajuda={carregando ? TEXTOS.carregando : TEXTOS.fiscalizacao.ajudaDaCompetencia}
      explicacao={<Explicacao termo="competencia" />}
      value={valor}
      disabled={carregando}
      onChange={(evento) => aoEscolher(evento.target.value)}
    >
      {rotuloDeTodas && <option value="">{rotuloDeTodas}</option>}
      {competencias.map((linha) => (
        <option key={linha.competencia} value={linha.competencia}>
          {rotuloDaOpcao(linha)}
        </option>
      ))}
    </CampoDeSelecao>
  )
}

/**
 * O texto de uma opção: o mês, quantos chamados e a ressalva de cobertura.
 *
 * A ressalva vai no texto da opção, e não num ícone ao lado, porque `<option>` não aceita marcação
 * — e porque quem usa leitor de tela ouve o texto da opção e mais nada. Um aviso que só existisse
 * depois de escolher chegaria tarde.
 */
function rotuloDaOpcao(linha: CompetenciaComDado): string {
  const chamados = `${linha.chamados} ${linha.chamados === 1 ? 'chamado fechado' : 'chamados fechados'}`
  const ressalva = linha.coberturaCompleta ? '' : ' — mês incompleto'
  return `${competenciaPorExtenso(linha.competencia)} · ${chamados}${ressalva}`
}
